import React, { useState, useEffect } from 'react';
import './App.css';

const POINT_BUTTONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const PLAYER_COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', 
  '#FFEAA7', '#DDA0DD', '#98D8C8', '#F7DC6F'
];

const PLAYER_ICONS = [
  '🎮', '🕹️', '🎯', '🎲', '🃏', '🎪',
  '🚀', '⭐', '🔥', '⚡', '🌟', '💎',
  '🦄', '🐉', '🦅', '🐺', '🦁', '🐯',
  '👑', '🏆', '🎊', '🎉', '🎈', '🎭',
  '🌈', '🔮', '⚔️', '🛡️', '🏹', '🎸',
  '🍕', '🍔', '🍟', '🍩', '🍪', '🎂'
];

function App() {
  const [gameStarted, setGameStarted] = useState(false);
  const [players, setPlayers] = useState([]);
  const [playerNames, setPlayerNames] = useState(['', '']);
  const [customPoints, setCustomPoints] = useState({});
  const [recentGames, setRecentGames] = useState([]);
  const [gameName, setGameName] = useState('');

  // Load game state from localStorage on component mount
  useEffect(() => {
    const savedGame = localStorage.getItem('gamePointsTracker');
    if (savedGame) {
      const gameData = JSON.parse(savedGame);
      setGameStarted(gameData.gameStarted);
      setPlayers(gameData.players || []);
      setPlayerNames(gameData.playerNames || ['', '']);
      setGameName(gameData.gameName || '');
    }
    
    const savedRecentGames = localStorage.getItem('recentGames');
    if (savedRecentGames) {
      setRecentGames(JSON.parse(savedRecentGames));
    }
  }, []);

  // Save game state to localStorage whenever it changes
  useEffect(() => {
    const gameData = {
      gameStarted,
      players,
      playerNames,
      gameName
    };
    localStorage.setItem('gamePointsTracker', JSON.stringify(gameData));
  }, [gameStarted, players, playerNames, gameName]);

  const addPlayer = () => {
    if (playerNames.length < 6) {
      setPlayerNames([...playerNames, '']);
    }
  };

  const removePlayer = (index) => {
    if (playerNames.length > 2) {
      const newNames = playerNames.filter((_, i) => i !== index);
      setPlayerNames(newNames);
    }
  };

  const updatePlayerName = (index, name) => {
    const newNames = [...playerNames];
    newNames[index] = name;
    setPlayerNames(newNames);
    
    // Auto-add new player input if user started typing and we're at the last input and under 6 players
    if (name.length === 1 && index === playerNames.length - 1 && playerNames.length < 6) {
      setPlayerNames([...newNames, '']);
    }
  };

  const startGame = () => {
    const validNames = playerNames.filter(name => name.trim() !== '');
    if (validNames.length >= 2) {
      // Create a shuffled copy of icons to ensure no duplicates
      const shuffledIcons = [...PLAYER_ICONS].sort(() => Math.random() - 0.5);
      
      const initialPlayers = validNames.map((name, index) => ({
        id: index,
        name: name.trim(),
        score: 0,
        color: PLAYER_COLORS[index % PLAYER_COLORS.length],
        icon: shuffledIcons[index % shuffledIcons.length]
      }));
      setPlayers(initialPlayers);
      setGameStarted(true);
    }
  };

  const saveGameToRecent = () => {
    if (players.length > 0) {
      const currentWinners = getWinners();
      const gameSnapshot = {
        id: Date.now(),
        date: new Date().toLocaleDateString(),
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        players: players.map(p => ({ name: p.name, score: p.score, color: p.color, icon: p.icon })),
        winners: currentWinners,
        winner: currentWinners[0], // Keep for backward compatibility
        gameName: gameName
      };
      
      const updatedRecentGames = [gameSnapshot, ...recentGames.slice(0, 4)]; // Keep only 5 recent games
      setRecentGames(updatedRecentGames);
      localStorage.setItem('recentGames', JSON.stringify(updatedRecentGames));
    }
  };

  const loadRecentGame = (gameSnapshot) => {
    const restoredPlayers = gameSnapshot.players.map((p, index) => ({
      id: index,
      name: p.name,
      score: p.score,
      color: p.color,
      icon: p.icon || '🎮' // Fallback icon for old saved games
    }));
    
    setPlayers(restoredPlayers);
    setPlayerNames(gameSnapshot.players.map(p => p.name));
    setGameName(gameSnapshot.gameName || '');
    setGameStarted(true);
    setCustomPoints({});
  };

  const resetGame = () => {
    if (gameStarted && players.length > 0) {
      saveGameToRecent();
    }
    
    // Preserve player names from current game
    const currentNames = players.length > 0 
      ? players.map(p => p.name)
      : playerNames.filter(name => name.trim() !== '');
    
    setGameStarted(false);
    setPlayers([]);
    setPlayerNames(currentNames.length >= 2 ? currentNames : ['', '']);
    setCustomPoints({});
    localStorage.removeItem('gamePointsTracker');
  };

  const updateScore = (playerId, points) => {
    setPlayers(players.map(player => 
      player.id === playerId 
        ? { ...player, score: Math.max(0, player.score + points) }
        : player
    ));
  };

  const handleCustomPointsChange = (playerId, value) => {
    setCustomPoints({
      ...customPoints,
      [playerId]: value
    });
  };

  const applyCustomPoints = (playerId, isPositive = true) => {
    const points = parseInt(customPoints[playerId] || 0);
    if (points > 0) {
      updateScore(playerId, isPositive ? points : -points);
      setCustomPoints({
        ...customPoints,
        [playerId]: ''
      });
    }
  };

  const getWinners = () => {
    if (players.length === 0) return [];
    const maxScore = Math.max(...players.map(p => p.score));
    return players.filter(player => player.score === maxScore);
  };

  if (!gameStarted) {
    return (
      <div className="app">
        <div className="setup-container">
          <h1 className="title">🎮 Game Points Tracker</h1>
          <p className="subtitle">Enter player names to start tracking points!</p>
          
          <div className="game-name-setup">
            <input
              type="text"
              placeholder="Game name (optional)"
              value={gameName}
              onChange={(e) => setGameName(e.target.value)}
              className="game-name-input"
              maxLength={30}
            />
          </div>
          
          <div className="player-setup">
            {playerNames.map((name, index) => (
              <div key={index} className="player-input-group">
                <input
                  type="text"
                  placeholder={`Player ${index + 1} name`}
                  value={name}
                  onChange={(e) => updatePlayerName(index, e.target.value)}
                  className="player-name-input"
                  maxLength={20}
                />
                {playerNames.length > 2 && (
                  <button 
                    onClick={() => removePlayer(index)}
                    className="remove-player-btn"
                    aria-label="Remove player"
                  >
                    ❌
                  </button>
                )}
              </div>
            ))}
            
            {playerNames.length < 6 && (
              <button onClick={addPlayer} className="add-player-btn">
                ➕ Add Player
              </button>
            )}
          </div>
          
          <button 
            onClick={startGame} 
            className="start-game-btn"
            disabled={playerNames.filter(name => name.trim() !== '').length < 2}
          >
            🚀 Start Game!
          </button>
        </div>
      </div>
    );
  }

  const winners = getWinners();
  const topScore = winners.length > 0 ? winners[0].score : 0;

  return (
    <div className="app">
      {winners.length > 0 && topScore > 0 && (
        <div className="winner-banner" style={{ 
          backgroundColor: winners[0].color + '20', 
          borderColor: winners[0].color,
          background: winners.length > 1 ? 
            `linear-gradient(90deg, ${winners.map(w => w.color + '20').join(', ')})` : 
            winners[0].color + '20'
        }}>
          🏆 {winners.length === 1 ? 
            `${winners[0].icon} ${winners[0].name} is leading with ${topScore} points` :
            `${winners.map(w => `${w.icon} ${w.name}`).join(', ')} are tied with ${topScore} points`
          }{gameName ? ` in ${gameName}` : ''}!
        </div>
      )}

      <div className="players-grid">
        {players.map((player) => (
          <div key={player.id} className="player-card" style={{ borderColor: player.color }}>
            <div className="player-header" style={{ backgroundColor: player.color }}>
              <div className="player-info">
                <span className="player-name">
                  <span className="player-icon">{player.icon}</span>
                  {player.name}
                </span>
                <span className="player-score">{player.score}</span>
              </div>
            </div>
            
            <div className="scoring-section">
              <div className="points-grid">
                <div className="points-row">
                  <span className="points-label">Add:</span>
                  {POINT_BUTTONS.slice(0, 5).map(points => (
                    <button
                      key={points}
                      onClick={() => updateScore(player.id, points)}
                      className="point-btn positive"
                    >
                      +{points}
                    </button>
                  ))}
                </div>
                <div className="points-row">
                  <span className="points-label"></span>
                  {POINT_BUTTONS.slice(5, 10).map(points => (
                    <button
                      key={points}
                      onClick={() => updateScore(player.id, points)}
                      className="point-btn positive"
                    >
                      +{points}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="custom-points">
                <div className="custom-input-group">
                  <input
                    type="number"
                    placeholder="99"
                    value={customPoints[player.id] || ''}
                    onChange={(e) => handleCustomPointsChange(player.id, e.target.value)}
                    className="custom-points-input"
                    min="1"
                    max="99"
                    maxLength="2"
                  />
                  <button
                    onClick={() => applyCustomPoints(player.id, true)}
                    className="custom-btn positive"
                    disabled={!customPoints[player.id] || customPoints[player.id] <= 0}
                  >
                    ➕
                  </button>
                  <button
                    onClick={() => applyCustomPoints(player.id, false)}
                    className="custom-btn negative"
                    disabled={!customPoints[player.id] || customPoints[player.id] <= 0}
                  >
                    ➖
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bottom-panel">
        <div className="game-header">
          <h1 className="game-title">🎮 Game Points</h1>
          <button onClick={resetGame} className="reset-btn">
            🔄 New Game
          </button>
        </div>

        {recentGames.length > 0 && (
          <div className="recent-games">
            <h3 className="recent-games-title">Recent Games</h3>
            <div className="recent-games-list">
              {recentGames.map((game) => (
                <div key={game.id} className="recent-game-item" onClick={() => loadRecentGame(game)}>
                  <div className="recent-game-info">
                    <div className="recent-game-date">{game.date} {game.time}</div>
                    <div className="recent-game-winner">
                      🏆 {game.winners && game.winners.length > 1 ? 
                        `${game.winners.map(w => `${w.icon} ${w.name}`).join(', ')} tied (${game.winners[0].score})` :
                        `${game.winner.icon ? game.winner.icon + ' ' : ''}${game.winner.name} (${game.winner.score})`
                      }{game.gameName ? ` - ${game.gameName}` : ''}
                    </div>
                  </div>
                  <div className="recent-game-players">
                    {game.players.map((player, index) => (
                      <span key={index} className="recent-player" style={{ color: player.color }}>
                        {player.icon ? player.icon + ' ' : ''}{player.name}: {player.score}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
